import {
  AdminCustomSelect,
  Brand,
  Button,
  Disclaimer,
  Div,
  H3,
  H5,
  lightTheme,
  MenulyticsReport,
  Modal,
  P,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { MenuHealthReportRow } from './components/MenuHealthReportRow';
import { MenulyticsMark } from './components/MenulyticsMark';
import { ShareReportModal } from './components/ShareReportModal';
import { useMenulyticsReportSocket } from './hooks/useMenulyticsReportSocket';
import { useMenulyticsReports } from './hooks/useMenulyticsReports';
import { reportBrandName, reportIsForBrand } from './menuHealthFormat';

type ReportBrand = Pick<Brand, 'id' | 'isLocation' | 'name'>;

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  locationId: string;
  /** The location itself, offered first as the location brand. */
  locationName?: string;
  /** The location's virtual brands; the location brand is never among them. */
  brands?: ReportBrand[];
  /**
   * Set on a brand's own screen: no brand picker, and only that brand's
   * reports in the list (the backend lists a whole location).
   */
  brand?: ReportBrand;
};

/**
 * Menulytics Menu Health reports of a location: generate one for a brand (it
 * covers every menu the brand uses), follow it over the socket, then open,
 * retry or share it with the owner.
 */
export const MenuHealthReportsModal = ({
  isOpen,
  closeModal,
  locationId,
  locationName = 'Location',
  brands = [],
  brand,
}: Props) => {
  // The location brand goes out as brand 0, which the backend accepts in place
  // of its real id - an id the location screen never gets to see.
  const options: ReportBrand[] = brand
    ? [brand]
    : [{ id: 0, name: locationName, isLocation: true }, ...brands];

  const [brandId, setBrandId] = useState<number | null>(null);
  const [sharing, setSharing] = useState<MenulyticsReport | null>(null);

  useEffect(() => {
    if (isOpen) {
      setBrandId(options[0].id);
    }
    // Only on opening: the brand list is the page's and does not change under it.
  }, [isOpen]);

  const selectedBrand = options.find((option) => option.id === brandId) ?? null;

  const {
    reports,
    isLoading,
    patch,
    refetch,
    generate,
    generating,
    retry,
    retryingId,
  } = useMenulyticsReports(locationId, isOpen);

  useMenulyticsReportSocket({
    locationId,
    enabled: isOpen,
    reports,
    patch,
    refetch,
  });

  const visibleReports = brand
    ? reports.filter((report) => reportIsForBrand(report, brand))
    : reports;

  const hasRows = !isLoading && visibleReports.length > 0;

  return (
    <>
      <Modal
        isOpen={isOpen && !sharing}
        closeModal={closeModal}
        logo={false}
        width="640px"
        // A scrolling modal cuts the Brand list at its bottom edge.
        // With at least one report below the select there is room for it
        // inside, so only an empty modal (nothing to scroll anyway) lets it
        // run past its edge.
        overflow={hasRows ? undefined : 'visible'}
        modalOverflow={hasRows ? undefined : 'visible'}
      >
        <Div $flex $column $gap="16px">
          {/* A partnership, not a vGrubs AI feature: their mark heads the
              modal in place of our red header. */}
          <Div $flex $column $alignCenter $gap="12px" $mb="8">
            <MenulyticsMark size={112} />
            <H3 $m="0">Analyze with Menulytics</H3>
          </Div>
          {!brand && (
            <AdminCustomSelect<number>
              label="Brand"
              value={brandId}
              onChange={(value) => {
                if (typeof value !== 'number') return;
                setBrandId(value);
              }}
              options={options.map((option) => ({
                // The location's own brand carries the location's name, which
                // reads like "the whole location" without the tag.
                name: option.isLocation
                  ? `${option.name} (location)`
                  : option.name,
                value: option.id,
              }))}
            />
          )}
          <Disclaimer $m="0" $left $color={lightTheme.darkGray}>
            Analyses all menus of this brand and the sales of each provider,
            over the last 30 days (excluding today). Takes about 30 seconds.
          </Disclaimer>
          <Button
            $selected
            $w="100%"
            $wSM="100%"
            $loading={generating}
            disabled={generating || !selectedBrand}
            onClick={() => selectedBrand && generate(selectedBrand.id)}
          >
            Analyze
          </Button>
        </Div>

        <H5 $m="32px 0 12px" $left>
          Reports
        </H5>
        {isLoading ? (
          <P $m="24px 0" $center $color={lightTheme.darkGray}>
            Loading...
          </P>
        ) : visibleReports.length === 0 ? (
          <P $m="24px 0" $center $color={lightTheme.darkGray}>
            No reports yet
          </P>
        ) : (
          visibleReports.map((report) => (
            <MenuHealthReportRow
              key={report.id}
              report={report}
              brandName={
                brand
                  ? undefined
                  : reportBrandName(report, brands, locationName)
              }
              onShare={setSharing}
              onRetry={retry}
              retrying={retryingId === report.id}
            />
          ))
        )}
      </Modal>

      <ShareReportModal
        locationId={locationId}
        report={sharing}
        closeModal={() => setSharing(null)}
        onShared={patch}
      />
    </>
  );
};
